import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import BackButton from '../../components/common/BackButton';
import { ATTRIBUTION_NOTICE } from '../../utils/attribution';
import { createAuthenticatedApiClient, setAuthHeader } from '../../config/api';

// Create authenticated API client for profile operations
const apiClient = createAuthenticatedApiClient();

/**
 * Component for editing user profile information
 */
const EditProfilePage: React.FC = () => {
  const { currentUser, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    aliasName: '',
    zipcode: '',
    emailPreferences: {
      newsletter: false,
      notifyOnInspiration: false,
      reminders: 'weekly',
      unsubscribed: false
    }
  });
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Initialize form data from current user profile
  useEffect(() => {
    if (currentUser) {
      setFormData({
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
        aliasName: currentUser.aliasName || '',
        zipcode: currentUser.location?.zipcode || '',
        emailPreferences: {
          newsletter: currentUser.emailPreferences?.newsletter || false,
          notifyOnInspiration: currentUser.emailPreferences?.notifyOnInspiration || false,
          reminders: currentUser.emailPreferences?.reminders || 'weekly',
          unsubscribed: currentUser.emailPreferences?.unsubscribed || false
        }
      });
    } else {
      // Redirect to login if not authenticated
      navigate('/login');
    }
  }, [currentUser, navigate]);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const checked = (e.target as HTMLInputElement).checked;
    
    if (name.includes('.')) {
      // Handle nested objects like emailPreferences
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      // Handle top-level fields
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Set authentication header before making the request
      setAuthHeader(apiClient);
      
      // Prepare data for API
      const updateData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        aliasName: formData.aliasName,
        location: {
          zipcode: formData.zipcode
        },
        emailPreferences: formData.emailPreferences
      };
      
      // Update profile via API using the configured apiClient
      const response = await apiClient.put<{ success: boolean; data: any }>('/contributor/profile', updateData);
      
      // Update local context if successful
      if (response.data && response.data.success && updateUserProfile) {
        updateUserProfile(response.data.data);
      }
      
      setSuccess('Profile updated successfully!');
      // Wait for 2 seconds to show success message before redirecting
      setTimeout(() => {
        navigate('/contributor/profile');
      }, 2000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <BackButton 
        label="Back to Profile" 
        onClick={() => navigate('/contributor/profile')}
      />
      
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h3 className="mb-0">Edit Profile</h3>
        </div>
        <div className="card-body">
          {/* Status Messages */}
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          {success && (
            <div className="alert alert-success" role="alert">
              {success}
            </div>
          )}
          
          {/* Profile Form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="firstName" className="form-label">First Name</label>
              <input
                type="text"
                className="form-control"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="lastName" className="form-label">Last Name</label>
              <input
                type="text"
                className="form-control"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="aliasName" className="form-label">
                Display Name / Alias 
                <small className="text-muted ms-1">(Shown publicly)</small>
              </label>
              <input
                type="text"
                className="form-control"
                id="aliasName"
                name="aliasName"
                value={formData.aliasName}
                onChange={handleChange}
              />
              <div className="form-text">{ATTRIBUTION_NOTICE}</div>
            </div>
            
            <div className="mb-3">
              <label htmlFor="zipcode" className="form-label">Zipcode</label>
              <input
                type="text"
                className="form-control"
                id="zipcode"
                name="zipcode"
                value={formData.zipcode}
                onChange={handleChange}
                maxLength={10}
              />
            </div>
            
            <h5 className="mt-4 mb-3">Email Preferences</h5>
            
            <div className="form-check mb-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="newsletter"
                name="emailPreferences.newsletter"
                checked={formData.emailPreferences.newsletter}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="newsletter">
                Receive monthly newsletter
              </label>
            </div>
            
            <div className="form-check mb-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="notifyOnInspiration"
                name="emailPreferences.notifyOnInspiration"
                checked={formData.emailPreferences.notifyOnInspiration}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="notifyOnInspiration">
                Notify me when someone is inspired by my note
              </label>
            </div>
            
            <div className="mb-3">
              <label htmlFor="reminders" className="form-label">Reminder Frequency</label>
              <select 
                className="form-select"
                id="reminders"
                name="emailPreferences.reminders"
                value={formData.emailPreferences.reminders}
                onChange={handleChange}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="never">Never</option>
              </select>
            </div>
            
            <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-4">
              <button
                type="button"
                className="btn btn-outline-secondary me-md-2"
                onClick={() => navigate('/contributor/profile')}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfilePage; 